import { Mark } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { commentAnchorService } from '../../services/commentAnchorService';

export const CommentAnchor = Mark.create({
    name: 'commentAnchor',

    addOptions() {
        return {
            HTMLAttributes: {},
            onCommentClick: null,
        };
    },

    addAttributes() {
        return {
            anchorId: {
                default: null,
                parseHTML: element => element.getAttribute('data-anchor-id'),
                renderHTML: attributes => {
                    if (!attributes.anchorId) {
                        return {};
                    }
                    return {
                        'data-anchor-id': attributes.anchorId,
                    };
                },
            },
            commentId: {
                default: null,
                parseHTML: element => element.getAttribute('data-comment-id'),
                renderHTML: attributes => {
                    if (!attributes.commentId) {
                        return {};
                    }
                    return {
                        'data-comment-id': attributes.commentId,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-anchor-id]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', HTMLAttributes, 0];
    },

    addProseMirrorPlugins() {
        const extension = this;
        
        return [
            new Plugin({
                key: new PluginKey('commentAnchorDecoration'),
                
                state: {
                    init(_, state) {
                        return DecorationSet.empty;
                    },
                    
                    apply(tr, decorationSet, oldState, newState) {
                        // Update decorations based on comment anchors
                        const anchors = commentAnchorService.getAnchorsForDocument('spec');
                        const decorations = [];
                        
                        anchors.forEach(anchor => {
                            if (anchor.isValid && anchor.startOffset < newState.doc.content.size) {
                                try {
                                    const from = Math.min(anchor.startOffset, newState.doc.content.size - 1);
                                    const to = Math.min(anchor.endOffset, newState.doc.content.size);
                                    
                                    if (from < to) {
                                        const className = commentAnchorService.getDecorationClass(anchor.id);
                                        decorations.push(
                                            Decoration.inline(from, to, {
                                                class: className,
                                                'data-anchor-id': anchor.id,
                                                'data-comment-id': anchor.id.replace('-anchor', '')
                                            })
                                        );
                                    }
                                } catch (error) {
                                    console.error('Error creating decoration for anchor:', anchor.id, error);
                                }
                            }
                        });
                        
                        return DecorationSet.create(newState.doc, decorations);
                    }
                },
                
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                    
                    handleClick(view, pos, event) {
                        const { target } = event;
                        const anchorElement = target.closest('[data-anchor-id]');
                        
                        if (anchorElement) {
                            const anchorId = anchorElement.getAttribute('data-anchor-id');
                            const commentId = anchorElement.getAttribute('data-comment-id');
                            
                            if (extension.options.onCommentClick) {
                                extension.options.onCommentClick(commentId, anchorId);
                            }
                            
                            return true;
                        }
                        
                        return false;
                    }
                },
                
                view() {
                    return {
                        update(view, prevState) {
                            // Reconcile anchors when document changes
                            if (!prevState || prevState.doc !== view.state.doc) {
                                commentAnchorService.reconcileSpecAnchors(view);
                            }
                        }
                    };
                }
            })
        ];
    },

    addCommands() {
        return {
            setCommentAnchor: (commentId) => ({ editor, commands }) => {
                const anchor = commentAnchorService.createSpecAnchor(editor, commentId);
                if (anchor) {
                    // Force decoration update
                    editor.view.dispatch(editor.state.tr);
                    return true;
                }
                return false;
            },
            
            removeCommentAnchor: (anchorId) => ({ editor, commands }) => {
                commentAnchorService.removeAnchor(anchorId);
                // Force decoration update
                editor.view.dispatch(editor.state.tr);
                return true;
            },
            
            navigateToCommentAnchor: (anchorId) => ({ editor }) => {
                return commentAnchorService.navigateToAnchor(anchorId, editor);
            }
        };
    }
});